import {DotProgress} from '@wandb/components';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {get} from 'lodash';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {
  useFileDownloadUrlQuery,
  useRunFilesQuery,
} from '../../generated/graphql';
import {
  type FileDownloadLocation,
  trackFileDownload,
} from '../../services/analytics/fileDownloadEvents';
import {MAX_NUM_FILES} from '../../util/constants';
import {useLoadFile} from '../../util/requests';
import * as Urls from '../../util/urls';
import FileBrowser from '../FileBrowser/FileBrowser';
import type {
  FileBrowserFile,
  UseFileBrowserLoadFile,
  UseFileBrowserLoadFileUrl,
} from '../FileBrowser/types';

// Handles multipart files and optional custom labels.
const CONSOLE_LOGS_FILENAME_REGEX = /^(logs\/)?output(.+)?\.log$/;

interface RunFilesProps {
  entityName: string;
  projectName: string;
  runName: string;
  path?: string;
  history: any;
  setSelectedRunName?: (name: string) => void;
}

const RunFiles = (props: RunFilesProps) => {
  const location = useLocation();
  const urlParams = useParams<{
    groupName?: string;
    sweepName?: string;
    tab?: string;
    runTab?: string;
  }>();

  const isGroupPage = urlParams.groupName != null;
  const isSweepPage = urlParams.sweepName != null;

  const {
    entityName,
    projectName,
    runName,
    history,
    path,
    setSelectedRunName = () => null,
  } = props;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlRunName = params.get('runName');
    if (urlRunName) {
      setSelectedRunName(urlRunName);
    }
  }, [setSelectedRunName, location.search]);

  const setFilePath = useCallback(
    (newPathArray: string[]) => {
      // files tab needs to be hard coded because could be in code tab with no code.codeArtifact, and we should
      // go to the files tab instead of code tab in this case
      history.push(
        urlParams.groupName && isGroupPage
          ? Urls.runGroup({
              entityName,
              projectName,
              name: urlParams.groupName,
              tab: urlParams.tab,
              runTab: 'files',
              filePath: newPathArray,
              runName,
            })
          : urlParams.sweepName && isSweepPage
            ? Urls.runSweep({
                entityName,
                projectName,
                name: urlParams.sweepName,
                tab: urlParams.tab,
                runTab: 'files',
                filePath: newPathArray,
                runName,
              })
            : Urls.runTabFile(entityName, projectName, runName, newPathArray)
      );
    },
    [
      entityName,
      projectName,
      runName,
      history,
      isGroupPage,
      urlParams.groupName,
      urlParams.sweepName,
      isSweepPage,
      urlParams.tab,
    ]
  );

  const handleDownload = useCallback(
    (file: FileBrowserFile) => {
      const location: FileDownloadLocation = urlParams.sweepName
        ? 'sweep-files'
        : urlParams.groupName
          ? 'group-files'
          : 'run-files';

      if (CONSOLE_LOGS_FILENAME_REGEX.test(file.name)) {
        trackFileDownload({
          fileType: 'log',
          isMultipartLogsFile: file.name.startsWith('logs/'),
          location,
        });
      }
    },
    [urlParams.groupName, urlParams.sweepName]
  );

  const runFilesQuery = useRunFilesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      entityName,
      projectName,
      runName,
      maxNumFiles: MAX_NUM_FILES,
    },
  });

  const artifactId = get(
    runFilesQuery,
    'data.project.run.codeArtifact.id',
    null
  );

  const files = useMemo(
    () =>
      runFilesQuery.data?.project?.run?.files?.edges
        .map(e => e.node)
        .filter(isNotNullOrUndefined)
        .map(node => ({
          ...node,
          updatedAt: node.updatedAt ? new Date(node.updatedAt) : null,
        })) ?? [],
    [runFilesQuery.data]
  );

  // The next couple of useCallback calls are a bit strange. The FileBrowser
  // components pass around a pair of hooks that are used for fetching the file
  // and its download url. For these hooks to work properly, they need some
  // data to be bound to them using a closure. The `useCallback` is here to
  // prevent a new reference identity from being created on every render.
  const useLoadRunFile: UseFileBrowserLoadFile = useCallback(
    (file, options) => {
      // oxlint-disable-next-line eslint-plugin-react-hooks/rules-of-hooks
      return useLoadFile(
        {entityName, projectName, runName},
        file.name,
        options
      );
    },
    [entityName, projectName, runName]
  );

  const useLoadRunFileUrl: UseFileBrowserLoadFileUrl = useCallback(
    file => {
      // oxlint-disable-next-line eslint-plugin-react-hooks/rules-of-hooks
      const result = useFileDownloadUrlQuery({
        variables: {
          entityName,
          projectName,
          runName,
          fileName: file.name,
        },
        fetchPolicy: 'no-cache',
      });

      if (!result.data) {
        return {loading: true};
      }

      const responseFile = get(result, 'data.project.run.files.edges[0].node');
      return {
        loading: false,
        file: responseFile,
      };
    },
    [entityName, projectName, runName]
  );

  const runFileScope = useMemo(
    () => ({entityName, projectName, runName}),
    [entityName, projectName, runName]
  );

  if (runFilesQuery.loading && files.length === 0) {
    return (
      <div className="p-48 flex flex-col gap-16 justify-center items-center">
        <DotProgress size="lg" aria-label="Loading files..." />
      </div>
    );
  }

  return (
    <FileBrowser
      artifactId={artifactId}
      runFileScope={runFileScope}
      totalFiles={runFilesQuery.data?.project?.run?.fileCount ?? 0}
      files={files}
      path={path}
      useLoadFile={useLoadRunFile}
      useLoadFileUrl={useLoadRunFileUrl}
      setFilePath={setFilePath}
      onDownload={handleDownload}
    />
  );
};

export default RunFiles;
